"use strict";
import { useTrendingPoolsCurrencyInfos, useTrendingTokensCurrencyInfos } from "appGraphql/data/ring/SearchRingWeb";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Flex, Text, TouchableArea, isWeb } from "ui/src";
import { useCurrencyInfosToTokenOptions } from "uniswap/src/components/TokenSelector/hooks/useCurrencyInfosToTokenOptions";
import { OnchainItemSectionName } from "uniswap/src/components/lists/OnchainItemList/types";
import { useNftSearchResultsToNftCollectionOptions } from "uniswap/src/components/lists/items/nfts/useNftSearchResultsToNftCollectionOptions";
import { useFavoriteWalletOptions } from "uniswap/src/components/lists/items/wallets/useFavoriteWalletOptions";
import { useOnchainItemListSection } from "uniswap/src/components/lists/utils";
import { useSearchPopularNftCollectionsQuery } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { UniverseChainId } from "uniswap/src/features/chains/types";
import { SearchModalList } from "uniswap/src/features/search/SearchModal/SearchModalList";
import {
  NUMBER_OF_RESULTS_LONG,
  NUMBER_OF_RESULTS_MEDIUM,
  NUMBER_OF_RESULTS_SHORT
} from "uniswap/src/features/search/SearchModal/constants";
import { useRecentlySearchedOptions } from "uniswap/src/features/search/SearchModal/hooks/useRecentlySearchedOptions";
import { SearchTab } from "uniswap/src/features/search/SearchModal/types";
import { clearSearchHistory } from "uniswap/src/features/search/searchHistorySlice";
import { isMobileApp } from "utilities/src/platform";
import noop from "utilities/src/react/noop";
function ClearButton({ onPress }) {
  const { t } = useTranslation();
  return <Flex height="100%" justifyContent="center" alignItems="center"><TouchableArea onPress={onPress}><Text color="$neutral2" variant="buttonLabel3">{t("common.clear")}</Text></TouchableArea></Flex>;
}
function useSectionsForNoQuerySearch({
  chainFilter,
  activeTab
}) {
  const dispatch = useDispatch();
  const recentlySearchedOptions = useRecentlySearchedOptions({
    chainFilter,
    activeTab,
    numberOfRecentSearchResults: NUMBER_OF_RESULTS_SHORT
  });
  const onPressClearSearchHistory = useCallback(() => {
    dispatch(clearSearchHistory());
  }, [dispatch]);
  const recentSearchSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.RecentSearches,
    options: recentlySearchedOptions,
    endElement: <ClearButton onPress={onPressClearSearchHistory} />
  });
  const numberOfTrendingTokens = activeTab === SearchTab.All ? isMobileApp ? NUMBER_OF_RESULTS_MEDIUM : NUMBER_OF_RESULTS_SHORT : NUMBER_OF_RESULTS_LONG;
  const {
    data: tokens,
    error: tokensError,
    refetch: refetchTokens,
    loading: loadingTokens
  } = useTrendingTokensCurrencyInfos(chainFilter ?? UniverseChainId.Mainnet);
  const trendingTokenOptions = useCurrencyInfosToTokenOptions({ currencyInfos: tokens });
  const trendingTokenSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.TrendingTokens,
    options: trendingTokenOptions?.slice(0, numberOfTrendingTokens)
  });
  const {
    data: pools,
    error: poolsError,
    refetch: refetchPools,
    loading: loadingPools
  } = useTrendingPoolsCurrencyInfos(chainFilter ?? UniverseChainId.Mainnet);
  const trendingPoolSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.TrendingPools,
    options: pools?.slice(0, numberOfTrendingTokens)
  });
  const skipPopularNftsQuery = isWeb || activeTab !== SearchTab.NFTCollections && activeTab !== SearchTab.All;
  const {
    data: popularNfts,
    loading: loadingPopularNfts,
    error: popularNftsError
  } = useSearchPopularNftCollectionsQuery({ skip: skipPopularNftsQuery });
  const popularNftOptions = useNftSearchResultsToNftCollectionOptions(popularNfts, chainFilter);
  const popularNftSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.PopularNFTCollections,
    options: popularNftOptions
  });
  const favoriteWalletsOptions = useFavoriteWalletOptions({ skip: activeTab !== SearchTab.Wallets });
  const favoriteWalletsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.FavoriteWallets,
    options: favoriteWalletsOptions
  });
  return useMemo(() => {
    let sections = [];
    switch (activeTab) {
      case SearchTab.Tokens:
        sections = [...recentSearchSection ?? [], ...trendingTokenSection ?? []];
        return {
          data: sections,
          loading: loadingTokens,
          error: tokensError,
          refetch: refetchTokens
        };
      case SearchTab.Pools:
        sections = [...recentSearchSection ?? [], ...trendingPoolSection ?? []];
        return {
          data: sections,
          loading: loadingPools,
          error: poolsError,
          refetch: refetchPools
        };
      case SearchTab.Wallets:
        return {
          data: [...recentSearchSection ?? [], ...favoriteWalletsSection ?? []],
          loading: false,
          error: void 0,
          refetch: noop
        };
      case SearchTab.NFTCollections:
        return {
          data: [...recentSearchSection ?? [], ...popularNftSection ?? []],
          // add recent nft searches
          loading: loadingPopularNfts,
          error: popularNftsError,
          refetch: noop
        };
      default:
      case SearchTab.All:
        if (isWeb) {
          sections = [
            ...recentSearchSection ?? [],
            ...trendingTokenSection ?? [],
            ...trendingPoolSection ?? []
            // ...getMockTrendingPoolsSection(3),
          ];
        }
        sections = [
          ...recentSearchSection ?? [],
          ...trendingTokenSection ?? [],
          ...trendingPoolSection ?? [],
          ...popularNftSection ?? []
        ];
        return {
          data: sections,
          loading: loadingTokens,
          error: tokensError,
          refetch: refetchTokens
        };
    }
  }, [
    activeTab,
    favoriteWalletsSection,
    loadingPopularNfts,
    loadingTokens,
    popularNftSection,
    popularNftsError,
    recentSearchSection,
    refetchTokens,
    tokensError,
    trendingTokenSection,
    loadingPools,
    poolsError,
    refetchPools,
    trendingPoolSection
  ]);
}
export const SearchModalNoQueryList = memo(function _SearchModalNoQueryList({
  chainFilter,
  activeTab,
  onSelect
}) {
  const { t } = useTranslation();
  const { data: sections, loading, error, refetch } = useSectionsForNoQuerySearch({ chainFilter, activeTab });
  return <SearchModalList
    errorText={t("token.selector.search.error")}
    hasError={Boolean(error)}
    loading={loading}
    refetch={refetch}
    sections={sections}
    searchFilters={{
      searchChainFilter: chainFilter,
      searchTabFilter: activeTab
    }}
    onSelect={onSelect}
  />;
});
